// "If I park here for N minutes starting now, what will it cost?"
//
// Walks the section's paid windows across the stay, charging only the minutes
// that actually fall inside a paid window (free stretches cost nothing), and
// checks the stay against the section's max-paid-stay limit.

import { pragueParts, addDays } from './time.js'
import { paidIntervalsForDate } from './freeNow.js'

/**
 * @typedef {Object} CostEstimate
 * @property {number}  cost          CZK for the whole stay (rounded)
 * @property {number}  paidMinutes   minutes charged
 * @property {number}  freeMinutes   minutes inside the stay that are free
 * @property {boolean} allowed       false if the paid part exceeds the max stay
 * @property {?number} maxHours      the section's max paid stay, if any
 * @property {boolean} fullyFree     true when nothing is charged
 * @property {Array}   segments      [{ startAbs, endAbs, minutes, rate }] charged stretches
 */

/**
 * Estimate the price of parking in `section` for `durationMin` starting at `startDate`.
 * @param {object} section normalized section properties
 * @param {Date} startDate
 * @param {number} durationMin
 * @returns {CostEstimate}
 */
export function estimateCost(section, startDate, durationMin) {
  const p = pragueParts(startDate)
  const startAbs = p.minutesOfDay
  const endAbs = startAbs + Math.max(0, durationMin)

  let cost = 0
  let paidMinutes = 0
  const segments = []

  // Merge charged stretches as we go so overlapping windows can't double-charge.
  const charged = []
  let ymd = { year: p.year, month: p.month, day: p.day }
  const daySpan = Math.ceil(endAbs / 1440) + 1
  for (let off = 0; off < daySpan; off++) {
    for (const iv of paidIntervalsForDate(section, ymd)) {
      const s = Math.max(off * 1440 + iv.start, startAbs)
      const e = Math.min(off * 1440 + iv.end, endAbs)
      if (e <= s) continue
      const rate = iv.rule?.price ?? section.price ?? 0
      charged.push({ s, e, rate })
    }
    ymd = addDays(ymd, 1)
  }
  charged.sort((a, b) => a.s - b.s)
  let cursor = -1
  for (const c of charged) {
    const s = Math.max(c.s, cursor)
    if (c.e <= s) continue // fully covered by an earlier window
    const minutes = c.e - s
    cost += (minutes / 60) * c.rate
    paidMinutes += minutes
    segments.push({ startAbs: s, endAbs: c.e, minutes, rate: c.rate })
    cursor = c.e
  }

  const maxHours = section.maxHours ?? null
  // The max-stay limit applies to the charged portion only.
  const allowed = maxHours == null || paidMinutes <= maxHours * 60 + 0.001

  return {
    cost: Math.round(cost),
    paidMinutes,
    freeMinutes: Math.max(0, durationMin - paidMinutes),
    allowed,
    maxHours,
    fullyFree: paidMinutes === 0,
    segments,
  }
}
